import { milestoneNotificationFix } from '~/server/common/constants';
import { NotificationCategory } from '~/server/common/enums';
import { createNotificationProcessor } from '~/server/notifications/base.notifications';
import { excludedReactorFilter } from '~/shared/utils/excluded-reactor-filter';
import { isV1Shaped, legacyId } from '~/server/notifications/comment.notifications';
import { getModelCommentThreadUrl } from '~/utils/comment-url-helpers';
import { humanizeList } from '~/utils/humanizer';
import { withDefiniteArticle } from '~/utils/string-helpers';

const commentReactionMilestones = [5, 10, 20, 50, 100] as const;
export const imageReactionMilestones = [5, 10, 20, 50, 100] as const;
const articleReactionMilestones = [5, 10, 20, 50, 100] as const;
// const reviewReactionMilestones = [5, 10, 20, 50, 100] as const;

export const reactionNotifications = createNotificationProcessor({
  'comment-reaction-milestone': {
    displayName: 'Comment reaction milestones',
    category: NotificationCategory.Milestone,
    prepareMessage: ({ details }) => ({
      message: `Your comment on ${details.modelName} has received ${details.reactionCount} reactions`,
      url: getModelCommentThreadUrl({
        modelId: details.modelId,
        commentId: details.rootCommentId,
      }),
    }),
    prepareQuery: ({ lastSent }) => `
      WITH milestones AS (
        SELECT * FROM (VALUES ${commentReactionMilestones.map((x) => `(${x})`).join(', ')}) m(value)
      ), affected AS (
        SELECT DISTINCT
          "commentId" affected_id
        FROM "CommentV3Reaction"
        WHERE "createdAt" > '${lastSent}'
      ), affected_value AS (
        SELECT
          a.affected_id,
          COUNT(r."commentId") reaction_count
        FROM "CommentV3Reaction" r
        JOIN affected a ON a.affected_id = r."commentId"
        GROUP BY a.affected_id
        HAVING COUNT(*) >= ${commentReactionMilestones[0]}
      ), reaction_milestone AS (
        -- Ids in the key and the permalink are the v1 ids: a comment renumbered by the backfill must
        -- not re-announce every milestone it already passed under its old id.
        SELECT
          c."userId" "ownerId",
          JSON_BUILD_OBJECT(
            'modelName', m.name,
            'modelId', m.id,
            'rootCommentId', ${legacyId('rt')},
            'commentId', ${legacyId('c')},
            'reactionCount', ms.value
          ) "details"
        FROM affected_value a
        JOIN "CommentV3" c ON c.id = a.affected_id AND ${isV1Shaped('c')} AND c."deletedAt" IS NULL
        JOIN "CommentV3" rt ON rt.id = c."rootId"
        JOIN "Model" m ON m.id = c."entityId"
        JOIN milestones ms ON ms.value <= a.reaction_count
        WHERE c."createdAt" > '${milestoneNotificationFix}'
      )
      SELECT
        CONCAT('comment-reaction-milestone:', details->>'commentId', ':', details->>'reactionCount') "key",
        "ownerId"    "userId",
        'comment-reaction-milestone' "type",
        details
      FROM reaction_milestone
      WHERE NOT EXISTS (SELECT 1 FROM "UserNotificationSettings" WHERE "userId" = "ownerId" AND type = 'comment-reaction-milestone')
    `,
  },
  'image-reaction-milestone': {
    displayName: 'Image reaction milestones',
    category: NotificationCategory.Milestone,
    prepareMessage: ({ details }) => {
      let message: string;
      if (details.version === 2) {
        let modelList: string | undefined;
        if (details.models) {
          const displayModels = details.models.slice(0, 2);
          const additionalModels = details.models.length - displayModels.length;
          modelList =
            additionalModels > 0
              ? displayModels.join(', ') + `, and ${additionalModels} more`
              : humanizeList(displayModels);
        }

        message = `Your image${modelList ? ` using ${modelList}` : ''} has received ${
          details.reactionCount
        } reactions`;
      } else {
        message = `Your ${
          details.reviewId ? 'review image' : 'example image'
        } on ${withDefiniteArticle(details.modelName)} model has received ${
          details.reactionCount
        } reactions`;
      }

      // `postId` is null for images that are not in a post (an article cover, for one:
      // 25,135 such images on prod as of 2026-09-03). Interpolated, that null became the
      // literal string `null`, and `/images/[imageId]` parses `postId` with `numericString`,
      // so `Number('null')` -> NaN -> a thrown ZodError during SSR -> a 500 on the link we
      // just mailed the user. Omit the param instead; the page reads a missing `postId` fine.
      const url =
        details.postId != null
          ? `/images/${details.imageId}?postId=${details.postId}`
          : `/images/${details.imageId}`;

      return { message, url };
    },
  },
  'article-reaction-milestone': {
    displayName: 'Article reaction milestones',
    category: NotificationCategory.Milestone,
    prepareMessage: ({ details }) => {
      const message = `Your article, "${details.articleTitle}" has received ${details.reactionCount} reactions`;

      return { message, url: `/articles/${details.articleId}` };
    },
    prepareQuery: async ({ lastSent, excludedUserIds }) => {
      // The displayed article count filters metric-suppressed accounts; a milestone that
      // did not would congratulate someone on a number their own page never shows. The
      // LENIENT reader on purpose — a failed read here degrades to the pre-exclusion
      // count, which is how this fired before, rather than to no notification at all.
      const excludedFilter = excludedReactorFilter(excludedUserIds ?? []);
      return `
      WITH milestones AS (
        SELECT * FROM (VALUES ${articleReactionMilestones.map((x) => `(${x})`).join(', ')}) m(value)
      ), affected AS (
        SELECT DISTINCT
          "articleId" affected_id
        FROM "ArticleReaction"
        WHERE "createdAt" > '${lastSent}'
      ), affected_value AS (
        SELECT
          a.affected_id,
          COUNT(r."articleId") reaction_count
        FROM "ArticleReaction" r
        JOIN affected a ON a.affected_id = r."articleId" ${excludedFilter}
        GROUP BY a.affected_id
        HAVING COUNT(*) >= ${articleReactionMilestones[0]}
      ), reaction_milestone AS (
        SELECT
          a."userId" "ownerId",
          JSON_BUILD_OBJECT(
            'articleId', a.id,
            'articleTitle', a.title,
            'reactionCount', ms.value
          ) "details"
        FROM affected_value af
        JOIN "Article" a on a.id = af.affected_id
        JOIN milestones ms ON ms.value <= af.reaction_count
            AND a."createdAt" > '${milestoneNotificationFix}'
      )
      SELECT
        CONCAT('article-reaction-milestone:', details->>'articleId', ':', details->>'reactionCount') "key",
        "ownerId"    "userId",
        'article-reaction-milestone' "type",
        details
      FROM reaction_milestone
      WHERE NOT EXISTS (SELECT 1 FROM "UserNotificationSettings" WHERE "userId" = "ownerId" AND type = 'article-reaction-milestone')
    `;
    },
  },
});
